import { useEffect, useState } from "react";
import { Search, Truck, ShoppingBag, RotateCcw, Check } from "lucide-react";
import { api, gbp, CATEGORIES as ALL_CATEGORIES, STORES } from "@/lib/api";
import { useMeta, fromPrice } from "@/lib/useMeta";
import { ProductCard } from "@/components/ProductCard";
import NewsletterSignup from "@/components/NewsletterSignup";
import HeroSlider from "@/components/HeroSlider";

const CATEGORIES = ["All", ...ALL_CATEGORIES];

const Hero = () => (
  <>
    <HeroSlider />
    <div className="bg-[#0F172A] py-3 overflow-hidden">
      <div className="ticker flex whitespace-nowrap gap-12 w-max font-mono text-xs uppercase tracking-[0.3em] text-[#F3D98B]">
        {Array.from({ length: 2 }).map((_, k) => (
          <span key={k} className="flex gap-12">{[...STORES, "Evri UK delivery", ...STORES, "Posted UK-wide"].map((s, i) => <span key={i}>✦ {s}</span>)}</span>
        ))}
      </div>
    </div>
  </>
);

const PromiseStrip = () => {
  const meta = useMeta();
  const from = fromPrice(meta);
  return (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 grid gap-6 md:grid-cols-3">
    {[
      [ShoppingBag, "Bought fresh for you", "When you order, we buy your items from our local M&S, Waitrose, Tesco or Sainsbury's."],
      [Truck, "Posted with Evri", `Postage priced by parcel weight${from ? ` from ${gbp(from)}` : ""}${meta?.free_over != null ? `, with free Standard delivery over ${gbp(meta.free_over)}` : ""}, anywhere in the UK.`],
      [RotateCcw, "Fair if it's sold out", "Missing items are refunded to your card. If nothing's in stock, we cancel with a full refund."],
    ].map(([Icon, t, d], i) => (
      <div key={t} className={`rounded-2xl p-6 ${i === 1 ? "bg-[#1B3626] text-white" : "bg-[#F7F3EB]"}`} data-testid={`promise-card-${i}`}>
        <Icon className={`w-6 h-6 ${i === 1 ? "text-[#F3D98B]" : "text-[#8C2220]"}`} />
        <p className="mt-4 font-serif text-xl font-semibold">{t}</p>
        <p className={`mt-2 text-sm leading-relaxed ${i === 1 ? "text-white/75" : "text-slate-600"}`}>{d}</p>
      </div>
    ))}
  </section>
  );
};

const PAGE = 48;

export default function Home() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [store, setStore] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(PAGE);
  const [freeFrom, setFreeFrom] = useState([]);

  useEffect(() => setLimit(PAGE), [cat, store, q, freeFrom]);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { limit: limit + 1 };
      if (freeFrom.length) params.free_from = freeFrom.join(",");
      if (cat !== "All") params.category = cat;
      if (store) params.store = store;
      if (q) params.q = q;
      setLoading(true);
      api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => setProducts([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, store, q, limit, freeFrom]);

  return (
    <div>
      <Hero />
      <PromiseStrip />
      <section id="newsletter" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="home-newsletter-banner">
        <div className="relative overflow-hidden rounded-3xl bg-[#1E2A44] grain grid md:grid-cols-12 gap-8 items-center p-8 sm:p-12">
          <div className="absolute -left-16 -bottom-20 w-72 h-72 rounded-full border-[28px] border-[#D4AF37]/10" />
          <div className="relative md:col-span-5">
            <p className="font-serif text-6xl sm:text-7xl font-bold text-[#D4AF37] leading-none">10% off</p>
            <p className="mt-3 text-[#F7F3EB] text-lg">your first order when you join the Pantry Letter.</p>
            <p className="mt-2 text-sm text-slate-400">Be first to hear about new hampers, seasonal gift shops and limited tins.</p>
          </div>
          <div className="relative md:col-span-7"><NewsletterSignup prefix="home-newsletter" /></div>
        </div>
      </section>
      <section id="shop" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 scroll-mt-28">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C2220]">The shelves</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">Shop the collection</h2>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chocolate, biscuits…" className="w-full rounded-full border border-[#E2D7C3] bg-white pl-11 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#8C2220]/30" data-testid="product-search-input" />
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${cat === c ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2D7C3] hover:border-[#8C2220]"}`} data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}>
              {c}
            </button>
          ))}
          <select value={store} onChange={(e) => setStore(e.target.value)} className="rounded-full px-4 py-2 text-sm border border-[#E2D7C3] bg-white" data-testid="store-filter-select">
            <option value="">All stores</option>
            {STORES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="allergen-filters">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-500 mr-1">Hide products with</span>
          {[["nuts", "Nuts"], ["gluten", "Gluten"], ["milk", "Milk"]].map(([k, label]) => {
            const on = freeFrom.includes(k);
            return (
              <button key={k} onClick={() => setFreeFrom(on ? freeFrom.filter((x) => x !== k) : [...freeFrom, k])} className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors flex items-center gap-1.5 ${on ? "bg-[#8C2220] text-white border-[#8C2220]" : "bg-white border-[#E2D7C3] hover:border-[#8C2220]"}`} aria-pressed={on} data-testid={`allergen-filter-${k}`}>
                {on && <Check className="w-3.5 h-3.5" />}{label}
              </button>
            );
          })}
          {freeFrom.length > 0 && <span className="text-xs text-slate-500" data-testid="allergen-filter-note">Includes "may contain". Products without allergen info are hidden. Always check the pack.</span>}
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
          {products.slice(0, limit).map((p, i) => <ProductCard key={p.id} p={p} index={i % PAGE} />)}
        </div>
        {products.length > limit && (
          <div className="mt-10 flex justify-center">
            <button onClick={() => setLimit(limit + PAGE)} disabled={loading} className="rounded-full border-2 border-[#0F172A] px-8 py-3 font-semibold hover:bg-[#0F172A] hover:text-white transition-colors disabled:opacity-50" data-testid="show-more-products">
              {loading ? "Loading…" : "Show more products"}
            </button>
          </div>
        )}
        {!loading && products.length === 0 && <p className="mt-10 text-slate-500" data-testid="no-products">No products found. Try another filter.</p>}
      </section>
    </div>
  );
}
